import React from 'react';
import { Button, Space, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FuelReceipt } from '../../types';
import { formatVietnamCurrency } from '../../utils/numberFormatter';

interface GetFuelReceiptColumnsParams {
  t: (key: string) => string;
  onEdit: (record: FuelReceipt) => void;
  onDelete: (id: number) => void;
  onUnlink: (id: number) => void;
  onStateTransition: (id: number, newState: 'WaitForProcess' | 'Created') => void;
  onViewImage: (id: number) => void;
}

export const getFuelReceiptColumns = ({
  t,
  onEdit,
  onDelete,
  onUnlink,
  onStateTransition,
  onViewImage
}: GetFuelReceiptColumnsParams): ColumnsType<FuelReceipt> => [
  {
    title: t('date'),
    dataIndex: 'refuelDate',
    key: 'refuelDate',
    width: 150,
    render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    sorter: (a, b) => dayjs(a.refuelDate).unix() - dayjs(b.refuelDate).unix()
  },
  {
    title: t('vehicle'),
    dataIndex: 'vehicleLicensePlate',
    key: 'vehicleLicensePlate',
    width: 120,
    sorter: (a, b) => a.vehicleLicensePlate.localeCompare(b.vehicleLicensePlate)
  },
  {
    title: t('liters'),
    dataIndex: 'liters',
    key: 'liters',
    width: 80,
    render: (value) => `${value}L`,
    sorter: (a, b) => a.liters - b.liters
  },
  {
    title: t('price.per.liter'),
    dataIndex: 'pricePerLiter',
    key: 'pricePerLiter',
    width: 120,
    render: (value) => formatVietnamCurrency(value),
  },
  {
    title: t('total.amount'),
    dataIndex: 'totalAmount',
    key: 'totalAmount',
    width: 130,
    render: (value) => formatVietnamCurrency(value),
    sorter: (a, b) => a.totalAmount - b.totalAmount
  },
  {
    title: t('gas.station'),
    dataIndex: 'gasStation',
    key: 'gasStation',
    width: 150,
    ellipsis: true
  },
  {
    title: t('location'),
    dataIndex: 'location',
    key: 'location',
    width: 150,
    ellipsis: true
  },
  {
    title: t('odometer'),
    dataIndex: 'odometer',
    key: 'odometer',
    width: 100,
    render: (value) => value ? `${value} km` : '-'
  },
  {
    title: t('state') || 'State',
    dataIndex: 'state',
    key: 'state',
    width: 100,
    render: (state) => (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: state === 'Created' ? '#52c41a' : '#faad14',
        color: 'white',
        fontSize: '12px'
      }}>
        {state === 'Created' ? (t('created') || 'Created') : (t('draft') || 'Draft')}
      </span>
    )
  },
  {
    title: t('receipt.image') || 'Receipt',
    key: 'receiptImage',
    width: 100,
    render: (_, record) => {
      return record.hasReceiptImage ? (
        <Button 
          size="small" 
          type="link"
          onClick={() => onViewImage(record.id)}
        >
          {t('view.image') || 'View'}
        </Button>
      ) : (
        <span style={{ color: '#999', fontSize: '12px' }}>
          {t('no.image') || 'No Image'}
        </span>
      );
    }
  },
  {
    title: t('created.by'),
    dataIndex: 'createdByUserName',
    key: 'createdByUserName',
    width: 120
  },
  {
    title: t('link.status'),
    key: 'linkStatus',
    width: 150,
    render: (_, record) => record.shippingRecordId ? (
      <Button 
        size="small" 
        type="link"
        onClick={() => window.open(`/shipping/edit/${record.shippingRecordId}`, '_blank')}
      >
        {record.shippingRecordInfo || `${t('linked')} #${record.shippingRecordId}`}
      </Button>
    ) : (
      <span className="text-muted">{t('unlinked')}</span>
    )
  },
  {
    title: t('actions'),
    key: 'actions',
    width: 280,
    fixed: 'right',
    render: (_, record) => (
      <Space>
        <Button
          icon={<EditOutlined />}
          onClick={() => onEdit(record)}
          size="small"
        />
        {record.state === 'WaitForProcess' ? (
          <Button
            size="small"
            type="primary"
            onClick={() => onStateTransition(record.id, 'Created')}
          >
            {t('finalize') || 'Finalize'}
          </Button>
        ) : (
          <Button
            size="small"
            onClick={() => onStateTransition(record.id, 'WaitForProcess')}
          >
            {t('to.draft') || 'To Draft'}
          </Button>
        )}
        {record.shippingRecordId && (
          <Popconfirm
            title={t('unlink') + '?'}
            onConfirm={() => onUnlink(record.id)}
            okText={t('yes')}
            cancelText={t('no')}
          >
            <Button
              size="small"
              type="link"
            >
              {t('unlink')}
            </Button>
          </Popconfirm>
        )}
        <Popconfirm
          title={t('delete.confirm')}
          onConfirm={() => onDelete(record.id)}
          okText={t('yes')}
          cancelText={t('no')}
        >
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
          />
        </Popconfirm>
      </Space>
    )
  }
];
